/**
 * Blog article body — contents list, scrollspy, and share-link copying.
 *
 * Shopify article HTML carries no anchor IDs, so the "In this article" card on
 * Figma board 74:2 cannot be rendered from Liquid. This walks the body after
 * paint, assigns stable IDs to the headings it finds, and builds the list.
 *
 * When an article has fewer real headings than the band's threshold the card
 * removes itself and the grid drops the sidebar column, so the legacy posts
 * that use <p><b>text</b></p> instead of <h2> show a clean two-column page
 * rather than an empty box. Bold paragraphs are deliberately NOT promoted to
 * headings: at least one live article wraps whole sentences in <b>, so that
 * heuristic would produce a nonsense contents list.
 *
 * Idempotent. Safe to load once per band and to re-run on section reload.
 */
(function () {
  'use strict';

  var DONE = 'blogTocReady';

  function slugify(text, used) {
    var base = String(text)
      .toLowerCase()
      .normalize('NFKD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '')
      .slice(0, 60) || 'section';
    var slug = base;
    var n = 2;
    while (used[slug]) { slug = base + '-' + n++; }
    used[slug] = true;
    return slug;
  }

  function prefersReducedMotion() {
    return window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }

  /* The sidebar column only earns its 240px if something visible survives in
     it. With no TOC and no CTA the grid would otherwise hold an empty track. */
  function settleSidebar(grid) {
    var side = grid.querySelector('[data-blog-side]');
    if (!side) { grid.setAttribute('data-sidebar', 'empty'); return; }
    var visible = Array.prototype.filter.call(side.children, function (el) {
      return !el.hasAttribute('hidden');
    });
    if (visible.length) {
      grid.removeAttribute('data-sidebar');
      side.removeAttribute('hidden');
    } else {
      grid.setAttribute('data-sidebar', 'empty');
      side.setAttribute('hidden', '');
    }
  }

  function buildToc(grid) {
    var body = grid.querySelector('[data-blog-body]');
    var toc = grid.querySelector('[data-blog-toc]');
    var list = grid.querySelector('[data-blog-toc-list]');
    if (!body || !toc || !list) { settleSidebar(grid); return; }

    var min = parseInt(grid.getAttribute('data-toc-min'), 10);
    if (isNaN(min) || min < 1) min = 3;

    var headings = Array.prototype.slice
      .call(body.querySelectorAll('h2, h3'))
      .filter(function (h) { return h.textContent.trim().length > 1; });

    if (headings.length < min) {
      toc.setAttribute('hidden', '');
      settleSidebar(grid);
      return;
    }

    var used = {};
    /* Respect an ID the author already set; only invent one when absent. */
    headings.forEach(function (h) {
      if (h.id) { used[h.id] = true; }
    });

    list.textContent = '';
    var links = [];
    headings.forEach(function (h) {
      if (!h.id) { h.id = slugify(h.textContent, used); }
      var li = document.createElement('li');
      if (h.tagName === 'H3') li.className = 'is-sub';
      var a = document.createElement('a');
      a.href = '#' + h.id;
      a.textContent = h.textContent.trim();
      a.addEventListener('click', function (e) {
        e.preventDefault();
        h.scrollIntoView({ behavior: prefersReducedMotion() ? 'auto' : 'smooth', block: 'start' });
        /* Move focus so keyboard and screen-reader users land where the click
           went, without the browser re-jumping past the scroll-margin. */
        h.setAttribute('tabindex', '-1');
        h.focus({ preventScroll: true });
        if (history.replaceState) history.replaceState(null, '', '#' + h.id);
      });
      li.appendChild(a);
      list.appendChild(li);
      links.push(a);
    });

    toc.removeAttribute('hidden');
    settleSidebar(grid);
    spy(headings, links);
  }

  /* Scrollspy: mark the heading whose section the reader is currently in.
     rootMargin pins the "current" line near the top of the viewport so the
     active item changes as a heading reaches it, not when it enters at the
     bottom. */
  function spy(headings, links) {
    if (!('IntersectionObserver' in window)) return;
    var visible = {};
    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (en) { visible[en.target.id] = en.isIntersecting; });
      var currentIdx = -1;
      for (var i = 0; i < headings.length; i++) {
        if (visible[headings[i].id]) { currentIdx = i; break; }
      }
      if (currentIdx === -1) {
        /* Between headings: the active one is the last that scrolled past. */
        for (var j = 0; j < headings.length; j++) {
          if (headings[j].getBoundingClientRect().top <= 120) currentIdx = j;
        }
      }
      links.forEach(function (a, k) {
        if (k === currentIdx) a.setAttribute('aria-current', 'true');
        else a.removeAttribute('aria-current');
      });
    }, { rootMargin: '-88px 0px -70% 0px', threshold: 0 });
    headings.forEach(function (h) { io.observe(h); });
  }

  function bindCopy(grid) {
    var btn = grid.querySelector('[data-copy-link]');
    if (!btn || btn.dataset.copyBound === 'true') return;
    btn.dataset.copyBound = 'true';
    var toast = grid.querySelector('[data-copy-toast]');
    btn.addEventListener('click', function () {
      var url = btn.getAttribute('data-url') || window.location.href;
      var done = function () {
        if (!toast) return;
        toast.textContent = 'Link copied';
        toast.setAttribute('data-on', '');
        setTimeout(function () { toast.removeAttribute('data-on'); toast.textContent = ''; }, 2000);
      };
      /* clipboard.writeText needs a secure context; the textarea fallback
         keeps this working on http previews and older Safari. */
      if (navigator.clipboard && window.isSecureContext) {
        navigator.clipboard.writeText(url).then(done).catch(function () { legacyCopy(url, done); });
      } else {
        legacyCopy(url, done);
      }
    });
  }

  function legacyCopy(text, done) {
    var ta = document.createElement('textarea');
    ta.value = text;
    ta.setAttribute('readonly', '');
    ta.style.cssText = 'position:absolute;left:-9999px;top:0;';
    document.body.appendChild(ta);
    ta.select();
    try { document.execCommand('copy'); done(); } catch (e) { /* nothing to do */ }
    document.body.removeChild(ta);
  }

  function init(scope) {
    var root = scope || document;
    var grids = root.querySelectorAll ? root.querySelectorAll('[data-blog-grid]') : [];
    Array.prototype.forEach.call(grids, function (grid) {
      bindCopy(grid);
      if (grid.dataset[DONE] === 'true') return;
      grid.dataset[DONE] = 'true';
      buildToc(grid);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { init(); });
  } else {
    init();
  }

  /* The theme editor replaces a section's markup wholesale on every save, and
     innerHTML-inserted scripts never execute, so without this the contents
     list would vanish the first time an editor touched the band. */
  document.addEventListener('shopify:section:load', function (e) { init(e.target); });
})();
